import { setFieldValue } from 'actions/ui/ui';
import {
  FETCH_SENDER_FAILURE,
  FETCH_SENDER_REQUEST,
  FETCH_SENDER_SUCCESS,
  SET_DEFAULT_FIELD_VALUES,
  UPDATE_SENDER_EMAIL,
} from 'constants/index';
import {
  getCurrentCountryCode,
  getCurrentLocale,
  getFieldValue,
  getSelectedOffer,
  isFetching,
} from 'selectors';
import { type FetchSendersParams, senders } from 'services/senders/senders';
import type { PayexThunkAction } from 'store/configureStore';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';

type SenderFieldsPayload = {
  entities?: {
    fields?: {
      sender_type?: {
        required?: boolean;
        value?: Array<{ value?: string }> | string;
      };
    };
  };
};

const getDefaultSenderType = (
  payload: SenderFieldsPayload,
  currentType: string,
): string | null => {
  const senderTypeField = payload.entities?.fields?.sender_type;
  const shouldSetDefaultType =
    senderTypeField?.required === true && !currentType;

  if (!shouldSetDefaultType) {
    return null;
  }

  const fieldValue = senderTypeField.value;
  return Array.isArray(fieldValue) && fieldValue[0]?.value
    ? (fieldValue[0].value as string)
    : null;
};

export const fetchSender =
  (country?: string): PayexThunkAction<Promise<void>> =>
  (dispatch, getState) => {
    const state = getState();
    const isFetchingSender = isFetching(state, 'sender');

    if (isFetchingSender) {
      return Promise.resolve();
    }

    dispatch({
      type: FETCH_SENDER_REQUEST,
    });

    const countryCode = country || (getCurrentCountryCode(state) as string);
    const locale = getCurrentLocale(state);
    const type = getFieldValue(state, 'sender_type') as string;

    const params: FetchSendersParams = {
      country: countryCode,
      locale,
      type: type !== '' ? type : 'individual',
    };

    const selectedOffer = getSelectedOffer(state);
    params.paymentMethod = selectedOffer.paymentMethodName;
    params.paymentMethodType = selectedOffer.paymentMethod;
    params.paymentOption = selectedOffer.paymentOption;
    params.priceAmount = selectedOffer.price?.value;
    params.priceCurrency = selectedOffer.price?.currency;
    params.purchaseCurrency = selectedOffer.purchase?.currency.code;

    return senders
      .fetch(params)
      .then(payload => {
        dispatch({
          type: FETCH_SENDER_SUCCESS,
          payload,
        });

        dispatch({
          type: SET_DEFAULT_FIELD_VALUES,
          payload: payload,
        });

        const defaultSenderType = getDefaultSenderType(payload, type);
        if (defaultSenderType) {
          dispatch(setFieldValue('sender_type', defaultSenderType));
        }
      })
      .catch(error => {
        errorNotifier.notify(error);

        dispatch({ type: FETCH_SENDER_FAILURE });
      });
  };

export const updateSenderEmail =
  (email: string): PayexThunkAction =>
  dispatch =>
    dispatch({ type: UPDATE_SENDER_EMAIL, payload: email });
